import { useMemo } from "react";
import { BENEFICIARY_TYPES, RURAL_URBAN_SPLIT, AGE_BANDS } from "../../data/ybf/beneficiaries";
import { getPillar } from "../../data/ybf/pillars";
import { STORIES } from "../../data/ybf/stories";
import StoryCard from "../../components/ybf/StoryCard";
import GenderSilhouettes from "../../components/ybf/GenderSilhouettes";
import { fmtInt } from "../../data/ybf/utils";
import { Users, Home, Building, Calendar, BookOpen, Mic, GraduationCap, Sprout, UserCheck, Palette, Factory, Briefcase, Heart, User, Laptop } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";

const TYPE_ICONS = {
  "School Students":   GraduationCap,
  "Teachers":          BookOpen,
  "Girl Learners":     Laptop,
  "Youth (Skilling)":  Briefcase,
  "Green Communities": Sprout,
};

const AGE_ICONS = {
  GraduationCap,
  BookOpen,
  Briefcase,
  Users,
};

export default function Beneficiaries() {
  const total = useMemo(
    () => BENEFICIARY_TYPES.reduce((s, b) => s + b.value, 0),
    []
  );
  const totalFemale = useMemo(
    () => BENEFICIARY_TYPES.reduce((s, b) => s + b.gender.female, 0),
    []
  );
  const totalMale = total - totalFemale;
  const femalePct = Math.round((totalFemale / total) * 100);

  const ruralUrbanData = [
    { name: "Rural", value: RURAL_URBAN_SPLIT.rural,  color: "#E2231A" },
    { name: "Urban", value: RURAL_URBAN_SPLIT.urban,  color: "#6B7280" },
  ];

  return (
    <div data-testid="ybf-beneficiaries" className="space-y-5 pb-8">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[#E2231A]" />
          <h1 className="text-2xl font-bold text-slate-900">Beneficiaries</h1>
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Who Lenovo CSR programmes are reaching — FY 2024-25
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Users}    label="Total beneficiaries" value={fmtInt(total)} accent="#1A1A1A" />
        <Tile icon={Users}    label="Women reached"        value={fmtInt(totalFemale)} sub={`${femalePct}% of total`} accent="#EC4899" />
        <Tile icon={Home}     label="Rural"                value={`${RURAL_URBAN_SPLIT.rural}%`} accent="#E2231A" />
        <Tile icon={Building} label="Urban"                value={`${RURAL_URBAN_SPLIT.urban}%`} accent="#6B7280" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-4">
        {/* Beneficiary Type — infographic cards (full width) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-900">By Beneficiary Type</h3>
          <p className="text-[11px] text-slate-500 mb-4">Distribution across primary segments</p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {BENEFICIARY_TYPES.map((b) => {
              const Icon = TYPE_ICONS[b.type] || Users;
              const share = Math.round((b.value / total) * 100);
              const p = getPillar(b.pillar);
              return (
                <div
                  key={b.type}
                  data-testid={`beneficiary-info-${b.type.replace(/\s+/g, "-")}`}
                  className="relative rounded-xl p-3 border border-slate-100 overflow-hidden flex flex-col items-center text-center hover:shadow-md transition-shadow"
                  style={{ background: `linear-gradient(180deg, ${b.color}12 0%, #FFFFFF 60%)` }}
                >
                  {/* Pillar chip */}
                  <span
                    className="absolute top-2 right-2 text-[8px] uppercase tracking-wider font-bold px-1 py-0.5 rounded"
                    style={{ background: `${p?.color}15`, color: p?.color }}
                  >
                    {p?.short}
                  </span>

                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mt-1 mb-2"
                    style={{ background: b.color, boxShadow: `0 4px 12px ${b.color}40` }}
                  >
                    <Icon size={22} className="text-white" strokeWidth={2} />
                  </div>

                  {/* Big number */}
                  <div className="text-2xl font-bold leading-none" style={{ color: b.color }}>
                    {fmtInt(b.value)}
                  </div>

                  {/* Label */}
                  <div className="text-[11px] font-semibold text-slate-800 mt-1.5 leading-tight">
                    {b.type}
                  </div>

                  {/* Share + bar */}
                  <div className="w-full mt-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-[9px] uppercase tracking-wider text-slate-500 font-medium">Share</span>
                      <span className="text-[10px] font-bold" style={{ color: b.color }}>{share}%</span>
                    </div>
                    <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${share}%`, background: b.color }}
                      />
                    </div>
                  </div>

                  {/* Gender split silhouettes */}
                  {b.gender.female + b.gender.male > 0 && (
                    <GenderSilhouettes female={b.gender.female} male={b.gender.male} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer summary */}
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <span>
              <span className="font-bold text-slate-900">{fmtInt(total)}</span> total beneficiaries across{" "}
              <span className="font-bold text-slate-900">{BENEFICIARY_TYPES.length}</span> primary segments
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-pink-500" /> Women
              <span className="w-2 h-2 rounded-full bg-blue-500 ml-2" /> Men
            </span>
          </div>
        </div>
      </div>

      {/* Bottom row — Age + Rural/Urban */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={14} className="text-slate-500" />
            <h3 className="text-sm font-bold text-slate-900">By Age Band</h3>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">Pictograms scale to reach within each age group</p>
          <div className="space-y-2.5">
            {AGE_BANDS.map((b) => {
              const Icon = AGE_ICONS[b.icon] || User;
              const headcount = Math.round((b.value / 100) * total);
              // Pictogram: 10 figures, fill proportional to value/10 (rounded)
              const filled = Math.max(1, Math.round(b.value / 10));
              return (
                <div
                  key={b.band}
                  data-testid={`age-band-${b.band}`}
                  className="relative rounded-xl border border-slate-100 overflow-hidden flex items-center gap-3 p-3 hover:shadow-sm transition-shadow"
                  style={{ background: `linear-gradient(90deg, ${b.color}10 0%, #FFFFFF 60%)` }}
                >
                  {/* Left: icon */}
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: b.color, boxShadow: `0 4px 10px ${b.color}40` }}
                  >
                    <Icon size={20} className="text-white" strokeWidth={2} />
                  </div>

                  {/* Middle: label + pictogram */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold text-slate-900 leading-tight">
                          {b.band} <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: b.color }}>· {b.label}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">{b.description}</div>
                      </div>
                      <div className="flex items-baseline gap-1 flex-shrink-0">
                        <span className="text-xl font-bold" style={{ color: b.color }}>{b.value}%</span>
                        <span className="text-[10px] text-slate-500 font-medium">· {fmtInt(headcount)}</span>
                      </div>
                    </div>

                    {/* Pictogram row */}
                    <div className="flex items-center gap-0.5 mt-1.5" aria-label={`Pictogram showing ${b.value}%`}>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <User
                          key={i}
                          size={11}
                          strokeWidth={2.25}
                          style={{
                            color: i < filled ? b.color : "#E2E8F0",
                            opacity: i < filled ? 1 : 0.9,
                          }}
                          fill={i < filled ? b.color : "transparent"}
                        />
                      ))}
                      <span className="text-[9px] text-slate-400 ml-1 font-medium">
                        {filled} in 10
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="text-sm font-bold text-slate-900 mb-3">Rural vs Urban</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={ruralUrbanData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {ruralUrbanData.map((r) => <Cell key={r.name} fill={r.color} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => `${v}%`} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2">
            {ruralUrbanData.map((r) => (
              <div key={r.name} className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                  <span className="text-[10px] text-slate-600 font-medium uppercase">{r.name}</span>
                </div>
                <div className="text-2xl font-bold text-slate-900 mt-0.5">{r.value}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stories from the field — compact cards */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[#E2231A]" />
            <h3 className="text-sm font-bold text-slate-900">Stories from the Field</h3>
          </div>
          <a
            href="/stories"
            className="text-[11px] font-semibold text-[#1A1A1A] hover:text-[#E2231A] transition-colors"
            data-testid="beneficiaries-see-all-stories"
          >
            See all {STORIES.length} stories →
          </a>
        </div>
        <p className="text-[11px] text-slate-500 mb-4">
          Voices of beneficiaries, captured via{" "}
          <span className="inline-flex items-center gap-0.5 font-semibold text-violet-700">
            <Mic size={10} /> Voice AI agents
          </span>
          .
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {STORIES.slice(0, 6).map((s) => (
            <StoryCard key={s.id} story={s} variant="compact" />
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`beneficiary-tile-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}10`, color: accent }}>
        <Icon size={18} />
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mt-3">{label}</div>
      <div className="text-2xl font-bold text-slate-900 leading-none mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
